import { create } from 'zustand'
import type { CanvasMode, RelationType } from '@/types'

// ── Types ─────────────────────────────────────────────────────────────────────
interface SelectionState {
  // ── Table selection ───────────────────────────────────────────────────────
  /**
   * Primary selected table. Always reflects the single selected table or the
   * "last clicked" table in a multi-selection. null when nothing is selected.
   *
   * Legacy equivalent: selectedTblId
   */
  selectedTableId: string | null

  /**
   * Full set of selected table IDs (single + multi-select).
   * Always consistent with selectedTableId:
   *   - single selection → Set has exactly 1 item = selectedTableId
   *   - multi-selection → Set has N items; selectedTableId = null
   *   - nothing selected → empty Set; selectedTableId = null
   *
   * Legacy equivalent: selectedTblIds (Set)
   */
  selectedTableIds: Set<string>

  // ── Relationship selection ────────────────────────────────────────────────
  /**
   * ID of the selected relationship. null when no relationship is selected.
   * Selecting a relationship always clears table selection.
   *
   * Legacy equivalent: selectedRelIdx / selectedRelationship
   */
  selectedRelationshipId: string | null

  // ── Canvas mode ───────────────────────────────────────────────────────────
  /**
   * Current canvas interaction mode.
   * - 'pan'    → drag to pan the canvas
   * - 'select' → drag to draw a selection rectangle
   *
   * Legacy equivalent: canvasMode
   */
  canvasMode: CanvasMode

  // ── Relationship creation ─────────────────────────────────────────────────
  /**
   * Whether the user is currently in relationship-creation mode (2-click flow).
   * Legacy equivalent: relMode
   */
  relMode: boolean

  /**
   * Source table ID after the user clicks the first table in rel-creation mode.
   * null when relMode is false or the user hasn't clicked yet.
   *
   * Legacy equivalent: relSrcId
   */
  relationshipSourceTableId: string | null

  /**
   * Currently selected relationship type for the creation flow.
   * Legacy equivalent: the selected button in #rel-bar
   */
  relType: RelationType

  // ── Table selection actions ───────────────────────────────────────────────

  /**
   * Select exactly one table, clearing all other selections.
   * Clears relationship selection.
   *
   * Legacy equivalent: setting selectedTblId + clearing selectedTblIds
   */
  selectTable: (id: string) => void

  /** Alias for selectTable — semantically "only this table". */
  selectOnlyTable: (id: string) => void

  /**
   * Toggle a table in/out of multi-selection.
   * If it was the only selected table, deselects it.
   * Clears relationship selection.
   */
  toggleTableSelection: (id: string) => void

  /**
   * Replace the entire selection set with the given list.
   * Clears relationship selection.
   */
  selectMultipleTables: (ids: string[]) => void

  /** Clear table selection only (relationship selection untouched). */
  clearTableSelection: () => void

  // ── Relationship selection actions ────────────────────────────────────────

  /**
   * Select a relationship. Clears table selection.
   * Pass null to deselect without selecting anything else.
   */
  selectRelationship: (id: string | null) => void

  /** Clear relationship selection only (table selection untouched). */
  clearRelationshipSelection: () => void

  // ── Combined clear ────────────────────────────────────────────────────────

  /**
   * Clear everything — tables, relationships.
   * Called on Escape, canvas click-on-empty, etc.
   *
   * Legacy equivalent: deselectAll
   */
  clearSelection: () => void

  // ── Canvas mode actions ───────────────────────────────────────────────────

  setCanvasMode: (mode: CanvasMode) => void

  // ── Relationship creation actions ─────────────────────────────────────────

  /**
   * Enter relationship-creation mode.
   * Optionally sets the source table and/or relationship type upfront.
   *
   * Legacy equivalent: enterRelMode / relMode = true
   */
  startRelationshipCreation: (sourceTableId?: string, type?: RelationType) => void

  /** Exit relationship-creation mode, clearing source table. */
  cancelRelationshipCreation: () => void

  /** Set the relationship type to create (shown in RelTypeBar). */
  setRelationshipType: (type: RelationType) => void

  // ── Legacy aliases (kept for code still in migration) ─────────────────────
  /** @deprecated Use selectMultipleTables */
  setMultiSelection: (ids: string[]) => void
  /** @deprecated Use clearSelection */
  deselectAll: () => void
  /** @deprecated Use selectRelationship */
  selectRelation: (id: string | null) => void
  /** @deprecated Use startRelationshipCreation */
  enterRelMode: (srcId?: string) => void
  /** @deprecated Use cancelRelationshipCreation */
  exitRelMode: () => void

  // ── Legacy field aliases (read-only, same value) ──────────────────────────
  /** @deprecated Use selectedRelationshipId */
  selectedRelId: string | null
  /** @deprecated Use relationshipSourceTableId */
  relSrcId: string | null
}

// ── Store ─────────────────────────────────────────────────────────────────────
export const useSelectionStore = create<SelectionState>()((set) => {
  // ── Internal helpers ────────────────────────────────────────────────────
  const onlyTable = (id: string) => ({
    selectedTableId:      id,
    selectedTableIds:     new Set([id]),
    selectedRelationshipId: null,
    selectedRelId:        null as string | null,
  })

  const clearTables = () => ({
    selectedTableId:  null as string | null,
    selectedTableIds: new Set<string>(),
  })

  const clearRel = () => ({
    selectedRelationshipId: null as string | null,
    selectedRelId:          null as string | null,
  })

  return {
    // ── Initial state ──────────────────────────────────────────────────────
    selectedTableId:        null,
    selectedTableIds:       new Set(),
    selectedRelationshipId: null,
    selectedRelId:          null,       // alias mirror
    canvasMode:             'pan',
    relMode:                false,
    relationshipSourceTableId: null,
    relSrcId:               null,       // alias mirror
    relType:                '1-N',

    // ── Table selection ────────────────────────────────────────────────────

    selectTable: (id) => set(onlyTable(id)),

    selectOnlyTable: (id) => set(onlyTable(id)),

    toggleTableSelection: (id) =>
      set((s) => {
        const next = new Set(s.selectedTableIds)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        const primaryId = next.size === 1 ? [...next][0] : null
        return {
          selectedTableIds:       next,
          selectedTableId:        primaryId,
          ...clearRel(),
        }
      }),

    selectMultipleTables: (ids) => {
      const next = new Set(ids)
      set({
        selectedTableIds:       next,
        selectedTableId:        ids.length === 1 ? ids[0] : null,
        ...clearRel(),
      })
    },

    clearTableSelection: () => set(clearTables()),

    // ── Relationship selection ─────────────────────────────────────────────

    selectRelationship: (id) =>
      set({
        selectedRelationshipId: id,
        selectedRelId:          id,
        ...clearTables(),
      }),

    clearRelationshipSelection: () => set(clearRel()),

    // ── Combined clear ─────────────────────────────────────────────────────

    clearSelection: () =>
      set({
        ...clearTables(),
        ...clearRel(),
      }),

    // ── Canvas mode ────────────────────────────────────────────────────────

    setCanvasMode: (mode) => set({ canvasMode: mode }),

    // ── Relationship creation ──────────────────────────────────────────────

    startRelationshipCreation: (sourceTableId, type) =>
      set((s) => ({
        relMode:                   true,
        relationshipSourceTableId: sourceTableId ?? null,
        relSrcId:                  sourceTableId ?? null,
        relType:                   type ?? s.relType,
      })),

    cancelRelationshipCreation: () =>
      set({
        relMode:                   false,
        relationshipSourceTableId: null,
        relSrcId:                  null,
      }),

    setRelationshipType: (type) => set({ relType: type }),

    // ── Legacy aliases ─────────────────────────────────────────────────────

    setMultiSelection: (ids) => {
      const next = new Set(ids)
      set({
        selectedTableIds:       next,
        selectedTableId:        ids.length === 1 ? ids[0] : null,
        ...clearRel(),
      })
    },

    deselectAll: () =>
      set({
        ...clearTables(),
        ...clearRel(),
      }),

    selectRelation: (id) =>
      set({
        selectedRelationshipId: id,
        selectedRelId:          id,
        ...clearTables(),
      }),

    enterRelMode: (srcId) =>
      set({
        relMode:                   true,
        relationshipSourceTableId: srcId ?? null,
        relSrcId:                  srcId ?? null,
      }),

    exitRelMode: () =>
      set({
        relMode:                   false,
        relationshipSourceTableId: null,
        relSrcId:                  null,
      }),
  }
})
